import React, { useCallback, useMemo } from "react";
import { compose, graphql } from "lib/apolloClient";
import _ from "lodash";
import { connect } from "react-redux";
import classNames from "classnames";

import ACLStore from "lib/aclStore";
import {
  getOrganizationId,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import {
  ELEMENT_TYPE_DP_SYLLABUS,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
} from "Constants/stringConstants";
import { mergeObjectArraysByDifferentKeysWithCustomValue } from "Utils/mergeTwoObjectArrays";
import getCurriculumIdsFilterForTags from "Utils/getCurriculumIdsFilterForTags";
import { useI18n } from "Hooks";
import StandardsTable from "StandardsTable";
import { DATA_GRID_COLUMN_TYPES } from "StandardsTable/modules/constants";
import { colors } from "Constants";
import { ENABLE_STANDARDS_OUTLINE } from "Constants/permissionConstants";
import { useStandardsTableProps } from "StandardsTable/hooks";

import {
  getCurriculumSubjectsSubjectGroupsGradesAndTagsQuery,
  getPublishedNodesFromNodeSetQuery,
  getPublishedNodesTaggedInDataFromNodeSetQuery,
  getPublishedNodesFromSetQuery,
  getSubjectVariantsQuery,
} from "./modules/Queries";
import {
  getMetadata,
  getColumnProperties,
  handleSelectionChange,
  getEdgesFromNodeMap as _getEdgesFromNodeMap,
  getPlannerElementConfigurations,
  getRootNodesToChildrenMap,
  getFilteredEdges,
  getFillColumn,
  isParentTaggingEnabledForType,
  getResolvedMinimalTreeSet,
  getFilterOverlayOptions,
} from "./modules/Utils";
import DatasetsTaggingHeader from "./components/DatasetsTaggingHeader";
import { useFilters } from "./providers/FilterProvider";
import useTreeManager from "./hooks/useTreeManager";
import classes from "./DatasetTaggingBody.scss";
import { FILTER_TYPES } from "./modules/Constants";

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const DatasetTaggingBody = props => {
  const {
    publishedEdges,
    publishedEdgesTaggedInData,
    hasTaggedInData,
    isData,
    isLoading,
    selectedRows,
    updateSelectedRows,
    grades = EMPTY_ARRAY,
    subjects = EMPTY_ARRAY,
    tags = EMPTY_ARRAY,
    subjectGroups = EMPTY_ARRAY,
    showLoaderOverHeader,
    currentColumns,
    defaultColumns,
    columnIdsForConfigureModal,
    updateColumns,
    config,
    actionHandlerFn: _actionHandlerFn = _.noop,
    shouldShowSideNavigation,
    setParentNodesCount,
    sideNavigationTab,
    properties: _properties = EMPTY_OBJECT,
    label,
    localFilters,
    localFilterVariables,
    plannerElementType,
    otherQueryVariables,
    treeMapRef,
    resolvedMinimalTree,
  } = props;

  const { t } = useI18n();

  const cellUtils = useMemo(() => {
    return {
      getMetadata: props =>
        getMetadata({
          ...props,
          selectedRows: new Set(selectedRows),
          config,
          hasTaggedInData,
          isParentTaggingEnabled: isParentTaggingEnabledForType({
            plannerElementType,
          }),
        }),
    };
  }, [plannerElementType, selectedRows, config, hasTaggedInData]);

  const { edges, extraMetadata } = useMemo(() => {
    const extraMetadata = { hasLockedEdges: false };
    const mergeConfig = [
      {
        arr: publishedEdges,
        key: "node.id",
        getValue: item => {
          const isLocked = _.get(item, "node.lockingInfo.isLocked", false);
          if (isLocked) extraMetadata.hasLockedEdges = true;

          return item;
        },
      },
      { arr: publishedEdgesTaggedInData, key: "node.id" },
    ];

    const edges = mergeObjectArraysByDifferentKeysWithCustomValue(mergeConfig);
    return { edges, extraMetadata };
  }, [publishedEdges, publishedEdgesTaggedInData]);

  const rootNodesToChildrenMap = useMemo(() => {
    // only calculate when we need localFiltering based on parentNodes in SideNavigation
    if (_.includes(localFilters, "parentNodes"))
      return getRootNodesToChildrenMap({ edges });
    return EMPTY_OBJECT;
  }, [localFilters, edges]);

  const filteredEdges = useMemo(() => {
    if (_.isEmpty(localFilterVariables)) return edges;
    //used when we enable localFilters
    return getFilteredEdges({
      edges,
      nodesFilters: localFilterVariables,
      rootNodesToChildrenMap,
    });
  }, [localFilterVariables, edges, rootNodesToChildrenMap]);

  const columnProperties = useMemo(
    () =>
      getColumnProperties({
        defaultColumns,
        properties: _properties,
        plannerElementType,
      }),
    [_properties, plannerElementType, defaultColumns]
  );
  const { resetFilters: _resetFilters } = useFilters();
  const {
    columns,
    nodeMap,
    properties,
    sideNavData,
    actionHandlerFn,
    originalNodeMap,
  } = useStandardsTableProps({
    edges: filteredEdges,
    columns: currentColumns,
    cellUtils,
    properties: columnProperties,
    actionHandlerFn: _actionHandlerFn,
  });
  const isPypStandards =
    plannerElementType === ELEMENT_TYPE_PYP_LEARNING_STANDARD;
  const initialNodes = getResolvedMinimalTreeSet({
    resolvedMinimalTree,
    plannerElementType,
  });

  const { updateTree } = useTreeManager({
    initialNodes,
    isPypStandards,
    treeMapRef,
  });

  const showLoader = !isData && isLoading;
  const showOutline =
    ACLStore.can(ENABLE_STANDARDS_OUTLINE) && !shouldShowSideNavigation;

  const gridClasses = classNames(classes.gridBody, {
    [classes.outlineHiddenGridBody]: !showOutline,
  });

  const onCellEdit = useCallback(
    ({ params }) => {
      const { selected, removed, newSelectedRows } = handleSelectionChange({
        nodeMap,
        params,
        selectedRows,
        updateSelectedRows,
        config,
      });
      if (!_.isEmpty(selected) || !_.isEmpty(removed)) {
        updateTree({
          addedNodes: selected,
          removedNodes: removed,
          nodeMap: originalNodeMap,
          selectedNodes: newSelectedRows,
        });
      }
      if (shouldShowSideNavigation) {
        const countChange = _.size(selected) - _.size(removed);
        setParentNodesCount(prev => ({
          ...prev,
          [sideNavigationTab]: _.get(prev, sideNavigationTab, 0) + countChange,
        }));
      }
    },
    [
      selectedRows,
      updateSelectedRows,
      config,
      nodeMap,
      sideNavigationTab,
      shouldShowSideNavigation,
      setParentNodesCount,
      originalNodeMap,
      updateTree,
    ]
  );

  const filterOverlayOptions = useMemo(
    () =>
      getFilterOverlayOptions({
        grades,
        subjects,
        tags,
        subjectGroups,
        defaultColumns,
        plannerElementType,
      }),
    [grades, subjects, tags, subjectGroups, defaultColumns, plannerElementType]
  );

  const resetFilters = useCallback(() => {
    const filtersToReset = [FILTER_TYPES.SEARCH, FILTER_TYPES.HIDE_LOCKED];
    _.forEach(filterOverlayOptions, ({ id }) => filtersToReset.push(id));
    _resetFilters({ filtersToReset });
  }, [filterOverlayOptions, _resetFilters]);

  const getEdgesFromNodeMap = useCallback(
    nodeMap => {
      return _getEdgesFromNodeMap({
        nodeMap,
        selectionColIndex: _.indexOf(columns, DATA_GRID_COLUMN_TYPES.SELECTION),
        selectedRows: new Set(selectedRows),
        config,
        t,
        plannerElementType,
      });
    },
    [columns, selectedRows, config, t, plannerElementType]
  );
  return (
    <div className={classes.bodyContainer}>
      <StandardsTable
        columns={columns}
        nodeMap={nodeMap}
        properties={properties}
        sideNavData={sideNavData}
        showLoader={showLoader}
        showOutline={showOutline}
        actionHandlerFn={actionHandlerFn}
      >
        <StandardsTable.Body bodyClass={gridClasses}>
          <StandardsTable.Header>
            <DatasetsTaggingHeader
              showLoaderOverHeader={showLoaderOverHeader}
              currentColumns={currentColumns}
              updateColumns={updateColumns}
              columnIdsForConfigureModal={columnIdsForConfigureModal}
              properties={properties}
              label={label}
              extraMetadata={extraMetadata}
              otherQueryVariables={otherQueryVariables}
              filterOverlayOptions={filterOverlayOptions}
            />
          </StandardsTable.Header>
          <StandardsTable.Grid
            resetFilters={resetFilters}
            onCellEdit={onCellEdit}
            getEdgesFromNodeMap={getEdgesFromNodeMap}
            freezeColumns={2}
            showLoader={showLoader}
            cellSelectionBorderColor={colors.strokeOne}
            emptyScreenTitle={t("common:no_results_found_on_search", {
              label,
            })}
            fillColumn={getFillColumn({ plannerElementType })}
          />
        </StandardsTable.Body>
      </StandardsTable>
    </div>
  );
};
const mapStateToProps = (state, props) => {
  const {
    plannerElementType,
    nodesFilters,
    initialFilterVariables,
    rootEdges,
  } = props;
  const plannerElementConfig = getPlannerElementConfigurations({
    plannerElementType,
  });
  const {
    useLocalFiltering,
    allowedLocalFilters,
    canTagFromAllGrades,
  } = plannerElementConfig;
  const orgId = getOrganizationId();
  const curriculumProgramId = getSelectedCurriculumId(state);
  let nodesQueryFilterVariable = { ...nodesFilters };
  if (!canTagFromAllGrades) {
    if (_.isEmpty(nodesQueryFilterVariable.grades)) {
      nodesQueryFilterVariable.grades = initialFilterVariables?.grades ?? [];
    }
  }
  const localFilters = useLocalFiltering ? allowedLocalFilters : EMPTY_ARRAY;
  nodesQueryFilterVariable = _.omit(nodesQueryFilterVariable, localFilters);
  //if we are apply local parentNodes filter, so we should fetch all the possible parentNodes, data beforeHand.
  if (_.includes(localFilters, "parentNodes")) {
    nodesQueryFilterVariable.parentNodes = _.map(rootEdges, "node.id");
  }
  return {
    orgId,
    curriculumProgramId,
    nodesQueryFilterVariable,
    localFilterVariables: _.pick(nodesFilters, localFilters),
    localFilters,
    plannerElementConfig,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getPublishedNodesFromNodeSetQuery, {
    name: "getPublishedNodesFromNodeSet",
    skip: ({ plannerElementNodeSetId }) => !plannerElementNodeSetId,
    options: ({
      plannerElementNodeSetId,
      nodesQueryFilterVariable,
      otherQueryVariables = EMPTY_OBJECT,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: plannerElementNodeSetId,
          nodesFilters: nodesQueryFilterVariable,
          ...otherQueryVariables,
        },
      };
    },
    props: ({ getPublishedNodesFromNodeSet }) => {
      const { node, loading } = getPublishedNodesFromNodeSet;
      const publishedEdges = _.get(
        node,
        "plannerElementNodeSetWrapperV2.publishedData.edges",
        []
      );
      return {
        publishedEdges,
        isData: !_.isEmpty(node),
        isLoading: loading,
      };
    },
  }),
  graphql(getPublishedNodesFromSetQuery, {
    name: "getPublishedNodesFromSet",
    skip: ({ plannerElementSetId }) => !plannerElementSetId,
    options: ({
      plannerElementSetId,
      nodeSetFilters,
      nodesQueryFilterVariable,
      otherQueryVariables = EMPTY_OBJECT,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: plannerElementSetId,
        nodeSetFilters,
        nodesFilters: nodesQueryFilterVariable,
        ...otherQueryVariables,
      },
    }),
    props: ({ getPublishedNodesFromSet }) => {
      const { node, loading } = getPublishedNodesFromSet;
      const publishedEdges = _.get(
        node,
        "plannerElementNodeSets.0.plannerElementNodeSetWrapperV2.publishedData.edges",
        []
      );
      return {
        publishedEdges,
        isData: !_.isEmpty(node),
        isLoading: loading,
      };
    },
  }),
  graphql(getCurriculumSubjectsSubjectGroupsGradesAndTagsQuery, {
    name: "getCurriculumSubjectsSubjectGroupsGradesAndTags",
    //for dp_syllabus subjectVariants tags
    skip: ({ skipFlags: { skipFlagsForFiltersData }, plannerElementType }) =>
      _.every(_.values(skipFlagsForFiltersData), Boolean) ||
      plannerElementType === ELEMENT_TYPE_DP_SYLLABUS,
    options: ({
      orgId,
      curriculumProgramId,
      skipFlags: { skipFlagsForFiltersData },
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        orgId,
        curriculumProgramId,
        genericTagsFilter: {
          types: ["GENERIC"],
          ...getCurriculumIdsFilterForTags({
            curriculumProgramId,
          }),
        },
        ...skipFlagsForFiltersData,
      },
    }),
    props: ({ getCurriculumSubjectsSubjectGroupsGradesAndTags, ownProps }) => {
      const { plannerElementConfig, initialFilters = EMPTY_OBJECT } = ownProps;
      const {
        node = EMPTY_OBJECT,
        loading,
      } = getCurriculumSubjectsSubjectGroupsGradesAndTags;
      const {
        subjects = EMPTY_ARRAY,
        grades = EMPTY_ARRAY,
        genericTagsV2: tags = EMPTY_ARRAY,
        subjectGroups = EMPTY_ARRAY,
      } = node;

      const { grades: gradesFilter } = initialFilters;

      let filteredGrades = grades;
      const { canTagFromAllGrades } = plannerElementConfig;
      if (!canTagFromAllGrades && !_.isEmpty(gradesFilter)) {
        filteredGrades = _.filter(grades, ({ id }) =>
          _.includes(gradesFilter, id)
        );
      }
      return {
        showLoaderOverHeader: _.isEmpty(node) && loading,
        subjects,
        grades: filteredGrades,
        tags,
        subjectGroups,
      };
    },
  }),
  graphql(getSubjectVariantsQuery, {
    name: "getSubjectVariants",
    skip: ({ plannerElementType, initialFilters = EMPTY_OBJECT }) => {
      const { subjects } = initialFilters;
      return (
        !_.isEqual(plannerElementType, ELEMENT_TYPE_DP_SYLLABUS) ||
        _.isEmpty(subjects)
      );
    },
    options: ({ initialFilters = EMPTY_OBJECT }) => {
      const { subjects } = initialFilters;
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          subjectId: _.first(subjects),
        },
      };
    },
    props({ getSubjectVariants, ownProps: { isData, isLoading } }) {
      const data = _.get(getSubjectVariants, "node", {});
      const subjectVariants = _.get(data, "variants", []);
      // In case of DP syllabus, we show subject variants as tags
      const tags = _.reduce(
        subjectVariants,
        (acc, item) => {
          if (item.level) {
            acc.push(item.level);
          }
          return acc;
        },
        []
      );

      return {
        tags,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes([1, 2], getSubjectVariants["networkStatus"]) || isLoading,
      };
    },
  }),
  graphql(getPublishedNodesTaggedInDataFromNodeSetQuery, {
    name: "getPublishedNodesTaggedInDataFromNodeSet",
    skip: ({
      plannerElementNodeSetId,
      skipFlags: { skipFlagsForTaggedInQuery },
    }) =>
      !plannerElementNodeSetId ||
      _.every(_.values(skipFlagsForTaggedInQuery), Boolean),
    options: ({
      plannerElementNodeSetId,
      plannerElementType,
      nodesQueryFilterVariable,
      nodeCountFilters,
      skipFlags: { skipFlagsForTaggedInQuery },
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          id: plannerElementNodeSetId,
          nodesFilters: nodesQueryFilterVariable,
          nodeCountFilters,
          isParentTaggingEnabled: isParentTaggingEnabledForType({
            plannerElementType,
          }),
          ...skipFlagsForTaggedInQuery,
        },
        context: {
          useHttpLink: true,
        },
      };
    },
    props: ({ getPublishedNodesTaggedInDataFromNodeSet }) => {
      const { node } = getPublishedNodesTaggedInDataFromNodeSet;
      const publishedEdgesTaggedInData = _.get(
        node,
        "plannerElementNodeSetWrapperV2.publishedData.edges",
        []
      );
      return {
        publishedEdgesTaggedInData,
        hasTaggedInData: true,
      };
    },
  })
)(DatasetTaggingBody);
